// src/pages/admin/UnitManagement.tsx
import React, { useState, useEffect } from 'react';
import {
  Plus, Search, Edit, Trash2, ChevronRight, ChevronDown,
  Building2, GraduationCap, FlaskConical, BookOpen,
  FolderTree, Loader2, Shield, Users,
} from 'lucide-react';
import { Button, Card, Breadcrumb, Modal, Input, Select } from '../../components/ui';
import toast from 'react-hot-toast';
import { adminService } from '../../services/admin';
import { Unit, User } from '../../types';

// Extended Unit interface to include potential leader relation if API returns it, or we map it
interface UnitWithLeader extends Unit {
  leader?: User;
}

const typeIcons: Record<string, React.ReactNode> = {
  UNIVERSITAS: <Building2 className="w-4 h-4" />,
  FAKULTAS: <Building2 className="w-4 h-4" />,
  PRODI: <GraduationCap className="w-4 h-4" />,
  PUSAT: <FlaskConical className="w-4 h-4" />,
  LEMBAGA: <BookOpen className="w-4 h-4" />,
  BAGIAN: <FolderTree className="w-4 h-4" />,
  LAB: <FlaskConical className="w-4 h-4" />,
  WAREK: <Shield className="w-4 h-4" />,
  TU: <Users className="w-4 h-4" />,
};

// Soft colored badges with border
const typeBadgeColors: Record<string, string> = {
  UNIVERSITAS: 'text-teal-700 bg-teal-50 border-teal-200',
  FAKULTAS: 'text-blue-700 bg-blue-50 border-blue-200',
  PRODI: 'text-purple-700 bg-purple-50 border-purple-200',
  TU: 'text-cyan-700 bg-cyan-50 border-cyan-200',
  PUSAT: 'text-orange-700 bg-orange-50 border-orange-200',
  LEMBAGA: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  BAGIAN: 'text-gray-700 bg-gray-100 border-gray-200',
  LAB: 'text-pink-700 bg-pink-50 border-pink-200',
  WAREK: 'text-indigo-700 bg-indigo-50 border-indigo-200',
};

// Solid colors for stat cards
const typeCardColors: Record<string, string> = {
  UNIVERSITAS: 'text-teal-700 bg-teal-50 border-teal-200',
  FAKULTAS: 'text-blue-700 bg-blue-50 border-blue-200',
  PRODI: 'text-purple-700 bg-purple-50 border-purple-200',
  TU: 'text-cyan-700 bg-cyan-50 border-cyan-200',
  PUSAT: 'text-orange-700 bg-orange-50 border-orange-200',
  LEMBAGA: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  BAGIAN: 'text-gray-700 bg-gray-100 border-gray-200',
  LAB: 'text-pink-700 bg-pink-50 border-pink-200',
  WAREK: 'text-indigo-700 bg-indigo-50 border-indigo-200',
};

const typeOptions = [
  { value: 'UNIVERSITAS', label: 'Universitas' },
  { value: 'FAKULTAS', label: 'Fakultas' },
  { value: 'PRODI', label: 'Program Studi' },
  { value: 'TU', label: 'Tata Usaha' },
  { value: 'PUSAT', label: 'Pusat/UPT' },
  { value: 'LEMBAGA', label: 'Lembaga' },
  { value: 'BAGIAN', label: 'Bagian' },
  { value: 'LAB', label: 'Laboratorium' },
  { value: 'WAREK', label: 'Wakil Rektor' },
];

// Tree Node Component — redesigned with card-style rows + dashed connectors
const TreeNode: React.FC<{
  node: UnitWithLeader;
  onEdit: (node: UnitWithLeader) => void;
  onDelete: (node: UnitWithLeader) => void;
  onAddChild: (parentNode: UnitWithLeader) => void;
  depth?: number;
  usersMap: Record<number, string>;
  isLast?: boolean;
}> = ({ node, onEdit, onDelete, onAddChild, depth = 0, usersMap, isLast = false }) => {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = node.children && node.children.length > 0;
  const indent = depth * 36;

  return (
    <div className="relative">
      {/* Dashed vertical line from parent */}
      {depth > 0 && (
        <div
          className="absolute border-l-2 border-dashed border-neutral-border/50"
          style={{
            top: 0,
            bottom: isLast ? '55%' : 0,
            left: `${indent - 18}px`,
          }}
        />
      )}

      <div
        className="relative flex items-center gap-3 mb-2"
        style={{ paddingLeft: `${indent}px` }}
      >
        {/* Horizontal connector */}
        {depth > 0 && (
          <div
            className="absolute border-t-2 border-dashed border-neutral-border/50"
            style={{
              top: '50%',
              left: `${indent - 18}px`,
              width: '18px',
            }}
          />
        )}

        {/* Row card */}
        <div
          className={`
            flex-1 flex items-center gap-3 py-2.5 px-4 rounded-xl border
            hover:shadow-md hover:border-teal-main/30 hover:bg-teal-main/[0.02]
            transition-all duration-150 group cursor-default
            ${depth === 0
              ? 'bg-gradient-to-r from-teal-50/60 to-white border-teal-main/30 shadow-sm'
              : 'bg-white border-neutral-border/40'}
          `}
        >
          {/* Expand/Collapse button */}
          <button
            onClick={() => setExpanded(!expanded)}
            className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md transition-colors ${hasChildren
              ? 'text-teal-main hover:bg-teal-main/10'
              : 'invisible'
              }`}
          >
            {expanded
              ? <ChevronDown className="w-4 h-4" />
              : <ChevronRight className="w-4 h-4" />}
          </button>

          {/* Type Badge */}
          <span className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] uppercase font-black border tracking-wide ${typeBadgeColors[node.type] || 'text-gray-700 bg-gray-100 border-gray-200'}`}>
            {typeIcons[node.type]}
            {node.type}
          </span>

          {/* Name & Code */}
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className={`text-sm font-bold truncate ${depth === 0 ? 'text-teal-dark' : 'text-neutral-text-primary'}`}>
              {node.name}
            </span>
            <span className="flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded bg-neutral-bg border border-neutral-border/60 text-neutral-text-secondary">
              {node.code}
            </span>
          </div>

          {/* Leader */}
          {node.leader_id && (
            <div className="hidden lg:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-neutral-bg border border-neutral-border/50 text-xs text-neutral-text-secondary">
              <Users className="w-3 h-3 text-teal-main flex-shrink-0" />
              <span className="truncate max-w-[120px]" title={`Pimpinan: ${usersMap[node.leader_id]}`}>
                {usersMap[node.leader_id] || `ID: ${node.leader_id}`}
              </span>
            </div>
          )}

          {node.secretary_id && (
            <div className="hidden lg:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-neutral-bg border border-neutral-border/50 text-xs text-neutral-text-secondary">
              <Users className="w-3 h-3 text-cyan-600 flex-shrink-0" />
              <span className="truncate max-w-[120px]" title={`Ka TU/Sekretaris: ${usersMap[node.secretary_id]}`}>
                {usersMap[node.secretary_id] || `ID: ${node.secretary_id}`}
              </span>
            </div>
          )}

          {/* Status */}
          <span className={`flex-shrink-0 text-[10px] font-bold px-2 py-1 rounded-md border ${node.is_active
            ? 'bg-success/10 text-success border-success/20'
            : 'bg-danger/10 text-danger border-danger/20'
            }`}>
            {node.is_active ? 'Aktif' : 'Nonaktif'}
          </span>

          {/* Actions — visible on hover */}
          <div className="flex-shrink-0 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => onEdit(node)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-500 text-blue-600 hover:text-white text-[11px] font-bold transition-colors border border-blue-200 hover:border-blue-500"
              title="Edit unit ini"
            >
              <Edit className="w-3.5 h-3.5" />
              Edit
            </button>
            <button
              onClick={() => onAddChild(node)}
              className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg bg-teal-50 hover:bg-teal-main text-teal-main hover:text-white text-[11px] font-bold transition-colors border border-teal-200 hover:border-teal-main"
              title="Tambah sub-unit di bawah unit ini"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
            {(!node.children || node.children.length === 0) && (
              <button
                onClick={() => onDelete(node)}
                className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg bg-red-50 hover:bg-danger text-danger hover:text-white text-[11px] font-bold transition-colors border border-red-200 hover:border-danger"
                title="Hapus unit ini"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Children */}
      {expanded && hasChildren && (
        <div>
          {node.children?.map((child, index) => (
            <TreeNode
              key={child.id}
              node={child}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddChild={onAddChild}
              depth={depth + 1}
              usersMap={usersMap}
              isLast={index === (node.children?.length || 0) - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const UnitManagement: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState<UnitWithLeader[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});

  const [editUnit, setEditUnit] = useState<UnitWithLeader | null>(null);
  const [parentUnit, setParentUnit] = useState<UnitWithLeader | null>(null);
  const [search, setSearch] = useState('');

  const [formData, setFormData] = useState({
    name: '', code: '', type: 'PRODI' as string, leader_id: '', secretary_id: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [unitsRes, usersRes] = await Promise.all([
        adminService.units.getAll(),
        adminService.users.getAll({ per_page: 9999 })
      ]);

      const mapChildren = (data: any): UnitWithLeader[] => {
        if (!Array.isArray(data)) return [];
        return data.map((item: any) => ({
          ...item,
          children: item.children_recursive ? mapChildren(item.children_recursive) : (item.children || [])
        }));
      };

      const unitsData = (unitsRes as any)?.data || unitsRes;
      const mappedUnits = mapChildren(unitsData);
      setUnits(mappedUnits);

      const usersData = (usersRes as any)?.data || usersRes;
      const usersList = Array.isArray(usersData) ? usersData : (usersData?.data || []);
      setUsers(usersList);

      const map: Record<number, string> = {};
      usersList.forEach((u: User) => map[u.id] = u.name);
      setUsersMap(map);

    } catch (e) {
      console.error(e);
      toast.error('Gagal memuat data unit');
    } finally {
      setLoading(false);
    }
  };

  const totalUnits = (() => {
    let count = 0;
    const countNodes = (nodes: Unit[]) => {
      nodes.forEach((n) => {
        count++;
        if (n.children) countNodes(n.children);
      });
    };
    countNodes(units);
    return count;
  })();

  const countByType = (type: string): number => {
    let count = 0;
    const countType = (nodes: Unit[]) => {
      nodes.forEach((n) => {
        if (n.type === type) count++;
        if (n.children) countType(n.children);
      });
    };
    countType(units);
    return count;
  };

  const openCreate = (parent?: UnitWithLeader) => {
    setEditUnit(null);
    setParentUnit(parent || null);
    setFormData({ name: '', code: '', type: 'PRODI', leader_id: '', secretary_id: '' });
    setShowModal(true);
  };

  const openEdit = (unit: UnitWithLeader) => {
    setEditUnit(unit);
    setParentUnit(null);
    setFormData({
      name: unit.name,
      code: unit.code,
      type: unit.type,
      leader_id: unit.leader_id ? unit.leader_id.toString() : '',
      secretary_id: unit.secretary_id ? unit.secretary_id.toString() : '',
    });
    setShowModal(true);
  };

  const handleDelete = async (unit: UnitWithLeader) => {
    if (window.confirm(`Hapus unit "${unit.name}"?`)) {
      try {
        await adminService.units.delete(unit.id);
        toast.success('Unit berhasil dihapus');
        fetchData();
      } catch (e) {
        toast.error('Gagal menghapus unit');
      }
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.code) {
      toast.error('Nama dan kode unit wajib diisi');
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        name: formData.name,
        code: formData.code,
        type: formData.type as any,
        leader_id: formData.leader_id ? Number(formData.leader_id) : undefined,
        secretary_id: formData.secretary_id ? Number(formData.secretary_id) : undefined,
      };

      if (!editUnit && parentUnit) {
        payload.parent_id = parentUnit.id;
      }

      if (editUnit) {
        await adminService.units.update(editUnit.id, payload);
        toast.success('Unit berhasil diperbarui');
      } else {
        await adminService.units.create(payload);
        toast.success('Unit berhasil ditambahkan');
      }
      setShowModal(false);
      fetchData();
    } catch (e) {
      console.error(e);
      toast.error('Gagal menyimpan unit');
    } finally {
      setSaving(false);
    }
  };

  const filterUnits = (nodes: UnitWithLeader[], query: string): UnitWithLeader[] => {
    if (!query) return nodes;
    return nodes.reduce((acc: UnitWithLeader[], node) => {
      const matches = node.name.toLowerCase().includes(query.toLowerCase()) || node.code.toLowerCase().includes(query.toLowerCase());
      const filteredChildren = node.children ? filterUnits(node.children, query) : [];

      if (matches || filteredChildren.length > 0) {
        acc.push({
          ...node,
          children: filteredChildren
        });
      }
      return acc;
    }, []);
  };

  const displayedUnits = filterUnits(units, search);

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Admin', href: '/admin' },
        { label: 'Unit Organisasi' },
      ]} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Struktur Unit Organisasi</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            <span className="font-bold text-teal-main">{totalUnits}</span> unit terdaftar dalam hierarki organisasi
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => openCreate()} icon={<Plus className="w-4 h-4" />}>
          Tambah Unit
        </Button>
      </div>

      {/* Stats — redesigned coloured stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-3">
        {typeOptions.map((t) => {
          const count = countByType(t.value);
          return (
            <div
              key={t.value}
              className={`relative overflow-hidden rounded-xl border p-3 flex flex-col gap-1 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 cursor-default ${typeCardColors[t.value] || 'text-gray-700 bg-gray-50 border-gray-200'}`}
            >
              {/* Watermark icon */}
              <div className="absolute -right-3 -top-3 opacity-10 pointer-events-none scale-150">
                {typeIcons[t.value]}
              </div>
              <div className="flex items-center justify-between gap-1">
                <div className="p-1.5 rounded-lg bg-white/60 shadow-sm backdrop-blur-sm">
                  {typeIcons[t.value]}
                </div>
                <span className="text-2xl font-black">{count}</span>
              </div>
              <p className="text-[10px] font-bold uppercase tracking-wide opacity-75 leading-tight">{t.label}</p>
            </div>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
        <input
          type="text"
          placeholder="Cari unit berdasarkan nama atau kode..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-neutral-border text-sm bg-white focus:outline-none focus:border-teal-main focus:ring-0 shadow-sm transition-colors"
        />
      </div>

      {/* Tree View */}
      <Card padding="md" className="shadow-sm">
        {/* Card header */}
        <div className="flex items-center justify-between mb-5 pb-4 border-b border-neutral-border/60">
          <h2 className="text-base font-bold text-neutral-text-primary flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-teal-main/10 flex items-center justify-center">
              <FolderTree className="w-4 h-4 text-teal-main" />
            </div>
            Hierarki Organisasi
          </h2>
          {search && (
            <span className="text-xs text-neutral-text-secondary">
              Menampilkan hasil untuk <strong>"{search}"</strong>
            </span>
          )}
        </div>

        {/* Tree content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 className="w-10 h-10 animate-spin text-teal-main" />
            <p className="text-sm text-neutral-text-secondary">Memuat struktur organisasi...</p>
          </div>
        ) : displayedUnits.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="w-16 h-16 rounded-2xl bg-neutral-bg flex items-center justify-center mx-auto mb-3">
              <FolderTree className="w-8 h-8 text-neutral-text-tertiary" />
            </div>
            <p className="text-base font-bold text-neutral-text-primary">Tidak ada unit ditemukan</p>
            <p className="text-sm text-neutral-text-secondary mt-1">
              {search ? 'Coba kata kunci pencarian yang lain.' : 'Belum ada unit dalam sistem.'}
            </p>
          </div>
        ) : (
          <div className="pt-1">
            {displayedUnits.map((unit, index) => (
              <TreeNode
                key={unit.id}
                node={unit}
                onEdit={openEdit}
                onDelete={handleDelete}
                onAddChild={(parent) => openCreate(parent)}
                usersMap={usersMap}
                isLast={index === displayedUnits.length - 1}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editUnit ? '✏️ Edit Unit' : '➕ Tambah Unit Baru'}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowModal(false)}>Batal</Button>
            <Button variant="primary" onClick={handleSave} loading={saving}>Simpan</Button>
          </>
        }
      >
        <div className="space-y-4">
          {parentUnit && (
            <div className="p-3 bg-teal-main/5 rounded-xl border border-teal-main/20">
              <p className="text-xs font-semibold text-teal-main">Sub-unit dari:</p>
              <p className="text-sm font-bold mt-1">{parentUnit.name} ({parentUnit.code})</p>
            </div>
          )}
          <Input
            label="Nama Unit"
            placeholder="Contoh: Prodi Teknik Informatika"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Kode Unit"
            placeholder="Contoh: TI"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
            required
          />
          <Select
            label="Tipe Unit"
            options={typeOptions}
            value={formData.type}
            onChange={(v) => setFormData({ ...formData, type: v })}
            required
          />
          <Select
            label="Pimpinan (Leader)"
            options={[
              { value: '', label: 'Pilih Pimpinan' },
              ...users.map(u => ({ value: u.id.toString(), label: u.name }))
            ]}
            value={formData.leader_id}
            onChange={(v) => setFormData({ ...formData, leader_id: v })}
          />
          <Select
            label="Sekretaris / Ka TU"
            options={[
              { value: '', label: 'Pilih Sekretaris / Ka TU' },
              ...users.map(u => ({ value: u.id.toString(), label: u.name }))
            ]}
            value={formData.secretary_id}
            onChange={(v) => setFormData({ ...formData, secretary_id: v })}
          />
        </div>
      </Modal>
    </div>
  );
};

export default UnitManagement;
